import { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { setRemember } from '../../lib/supabaseClient';
import { Button } from '../../components/ui/Button';
import { EyeIcon, EyeOffIcon } from '../../components/ui/IconField';

// Light-themed text input for the white form panel.
function TextField({ right, className = '', ...props }) {
  return (
    <div className="relative">
      <input
        className={`w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-[15px] text-slate-800 placeholder:text-slate-400
          transition focus:border-brand focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand/25 ${right ? 'pr-11' : ''} ${className}`}
        {...props}
      />
      {right && <span className="absolute right-2.5 top-1/2 -translate-y-1/2">{right}</span>}
    </div>
  );
}

export default function Login() {
  const { session, mfaRequired, getMfaStatus, enrollMfa, verifyMfa, login, logout, sendPasswordReset } = useAuth();
  const [mode, setMode] = useState('signin'); // 'signin' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [remember, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  // Authenticator-app 2FA step (shown when the password is right but the session
  // hasn't been verified with the app's 6-digit code yet). First login shows a QR
  // setup ('setup'); later logins just ask for the current code ('code').
  const [code, setCode] = useState('');
  const [mfaView, setMfaView] = useState(null); // null = loading, 'setup' | 'code'
  const [qr, setQr] = useState('');
  const [manualKey, setManualKey] = useState('');
  const initedMfa = useRef(false);
  const mfaStep = !!session && mfaRequired;

  // Work out which view to show, once, when the 2FA step first appears.
  useEffect(() => {
    if (mfaStep && !initedMfa.current) {
      initedMfa.current = true;
      getMfaStatus()
        .then((s) => {
          if (s.enrolled) { setMfaView('code'); return null; }
          return enrollMfa().then((e) => { setQr(e.qr); setManualKey(e.secret); setMfaView('setup'); });
        })
        .catch((err) => { setError(err.message); setMfaView('code'); });
    }
    if (!session) { initedMfa.current = false; setMfaView(null); setCode(''); setQr(''); setManualKey(''); }
  }, [mfaStep, session, getMfaStatus, enrollMfa]);

  if (session && !mfaRequired) return <Navigate to="/" replace />;

  const submitSignin = async (e) => {
    e.preventDefault();
    setError(''); setNotice(''); setBusy(true);
    setRemember(remember); // choose where the session is stored before signing in
    try { await login(email, password); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError(''); setNotice(''); setBusy(true);
    try { await verifyMfa(code); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const backToSignin = async () => {
    setError(''); setNotice(''); setCode('');
    await logout();
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError(''); setNotice(''); setBusy(true);
    try {
      await sendPasswordReset(email);
      setNotice(`If an account exists for ${email}, a password-reset link is on its way.`);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const switchMode = (next) => { setMode(next); setError(''); setNotice(''); };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-[#eef0fc] via-[#f3f1fb] to-[#e9e4f6] px-4 py-8">
      <div
        className="grid w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-slate-900/5 lg:max-w-4xl lg:grid-cols-2 lg:min-h-[520px]"
        style={{ animation: 'fadeUp 0.5s ease-out' }}
      >
        {/* Form panel */}
        <div className="flex flex-col justify-center p-8 sm:p-10 lg:p-11">
          <img src="/cysops-logo.png" alt="CysOps" className={`${mfaStep ? 'mt-0' : '-mt-8 sm:-mt-12'} h-16 sm:h-20 w-auto self-center object-contain`} />

          <h1 className="mt-6 text-3xl font-bold tracking-tight text-slate-900">
            {mfaStep ? (mfaView === 'setup' ? 'Set up 2FA.' : 'Two-step verification.') : 'Welcome back.'}
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">
            {mfaStep
              ? (mfaView === 'setup'
                ? 'Scan the QR code with Google Authenticator, Authy, or Microsoft Authenticator'
                : 'Enter the 6-digit code from your authenticator app')
              : mode === 'signin' ? 'Sign in to your reseller dashboard' : 'Reset your password'}
          </p>

          {error && <div className="mt-5 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 ring-1 ring-red-200">{error}</div>}
          {notice && <div className="mt-5 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700 ring-1 ring-green-200">{notice}</div>}

          {mfaStep ? (
            <form onSubmit={submitCode} className="mt-6 space-y-4">
              {mfaView === null && <p className="text-sm text-slate-500">Preparing…</p>}

              {mfaView === 'setup' && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-center">
                    {qr && (
                      <img
                        src={qr}
                        alt="Scan with your authenticator app"
                        className="h-44 w-44 rounded-lg bg-white p-1.5 ring-1 ring-slate-200"
                      />
                    )}
                  </div>
                  <p className="mt-3 text-center text-xs text-slate-500">
                    Can't scan? Add it manually with this key:
                    <span className="mt-1 block break-all font-mono text-[11px] font-semibold text-slate-700">{manualKey}</span>
                  </p>
                </div>
              )}

              {mfaView && (
                <>
                  <div>
                    <label htmlFor="mfa-code" className="sr-only">Authenticator code</label>
                    <TextField
                      id="mfa-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="••••••"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                      className="text-center text-2xl tracking-[0.5em] font-semibold"
                      autoFocus={mfaView === 'code'}
                      required
                    />
                  </div>
                  <Button type="submit" disabled={busy || code.length !== 6} className="w-full rounded-full py-3 uppercase tracking-wide">
                    {busy ? 'Verifying…' : mfaView === 'setup' ? 'Activate & sign in' : 'Verify & sign in'}
                  </Button>
                </>
              )}

              <button type="button" onClick={backToSignin} className="block text-sm font-medium text-slate-500 hover:text-slate-700">
                ← Back to sign in
              </button>
              {mfaView === 'code' && (
                <p className="text-xs text-slate-400">Lost access to your authenticator app? Ask your administrator to reset 2FA for your account.</p>
              )}
            </form>
          ) : (
          <form onSubmit={mode === 'signin' ? submitSignin : submitReset} className="mt-6 space-y-4">
            {mode === 'signin' ? (
              <>
                <div>
                  <label htmlFor="login-email" className="sr-only">Work email</label>
                  <TextField
                    id="login-email" type="email" placeholder="Work email"
                    value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                  />
                </div>
                <div>
                  <label htmlFor="login-password" className="sr-only">Password</label>
                  <TextField
                    id="login-password" type={showPw ? 'text' : 'password'} placeholder="Password"
                    value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
                    right={(
                      <button type="button" onClick={() => setShowPw((v) => !v)} className="p-1 text-slate-400 hover:text-slate-600" aria-label={showPw ? 'Hide password' : 'Show password'}>
                        {showPw ? <EyeOffIcon /> : <EyeIcon />}
                      </button>
                    )}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand/40"
                    />
                    Remember me
                  </label>
                  <button type="button" onClick={() => switchMode('forgot')} className="text-sm font-medium text-brand hover:text-brand-dark">
                    Forgot password?
                  </button>
                </div>

                <Button type="submit" disabled={busy} className="w-full rounded-full py-3 uppercase tracking-wide">
                  {busy ? 'Signing in…' : 'Sign in'}
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-slate-500">Enter your account email and we'll send you a link to reset your password.</p>
                <div>
                  <label htmlFor="reset-email" className="sr-only">Email</label>
                  <TextField
                    id="reset-email" type="email" placeholder="Work email"
                    value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                  />
                </div>
                <Button type="submit" disabled={busy} className="w-full rounded-full py-3 uppercase tracking-wide">
                  {busy ? 'Sending…' : 'Send reset link'}
                </Button>
                <button type="button" onClick={() => switchMode('signin')} className="block w-full text-center text-sm font-medium text-slate-500 hover:text-slate-700">
                  ← Back to sign in
                </button>
              </>
            )}
          </form>
          )}
        </div>

        {/* Hero panel — CysOps positioning */}
        <div className="relative hidden overflow-hidden bg-gradient-to-br from-[#677BE6] to-[#7450A9] p-10 text-white lg:flex lg:flex-col lg:justify-center">
          <div className="pointer-events-none absolute -top-12 -right-12 h-60 w-60 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -bottom-20 left-1/4 h-72 w-72 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute top-1/3 right-8 h-40 w-40 rounded-full bg-white/10" />

          <div className="relative">
            <h2 className="text-4xl font-extrabold leading-[1.1] tracking-tight">
              Managed Services.<br />Automated
            </h2>
            <p className="mt-4 max-w-md text-base text-white/80">
              AI-driven hyperautomation for MSPs &amp; MSSPs.
            </p>

            <div className="mt-8 grid max-w-md grid-cols-2 gap-3">
              <div className="rounded-2xl bg-white/10 p-4 ring-1 ring-white/15 backdrop-blur-sm">
                <div className="text-lg font-bold">Automated</div>
                <div className="mt-0.5 text-xs text-white/70">Billing &amp; monitoring</div>
              </div>
              <div className="rounded-2xl bg-white/10 p-4 ring-1 ring-white/15 backdrop-blur-sm">
                <div className="text-lg font-bold">Faster</div>
                <div className="mt-0.5 text-xs text-white/70">Response &amp; triage</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
